import { useEffect } from 'react';

import { DisplayAdsBiddingScript } from '@xing-com/crate-ads-display-ads';
import { Redirect, useLocation } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import {
  TeaserOnSide,
  FindJobsSearchBar,
} from '@xing-com/crate-jobs-pages-find-jobs-search-bar';
import { Head } from '@xing-com/crate-startpage-logged-out-head';
import { JobsSection } from '@xing-com/crate-startpage-logged-out-jobs-section-package';
import { NativeAppsSection } from '@xing-com/crate-startpage-logged-out-native-apps-section';
import { SeoSection } from '@xing-com/crate-startpage-logged-out-seo-section';
import { XingletLoader } from '@xing-com/crate-xinglet';
import { useExperiment } from '@xing-com/hub';
import {
  Container as LayoutPrimitivesContainer,
  Section,
} from '@xing-com/layout-primitives';

import { AdSection } from './ad-section';
import { CompaniesSection } from './companies-section';
import { DeepDiveSection } from './deep-dive-section';
import { RecruitersSection } from './recruiters-section';
import { RegisterSection } from './register-section';
import { SearchInspirationSection } from './search-inspiration-section';
import {
  StyledJobsSection,
  StyledSwimpSection,
} from './startpage-logged-out.styles';
import {
  useTrackPageView,
  useTrackClickStoreButton,
  useTrackClickSerpCategory,
} from './tracking';

const rootLanguages = ['de', 'en', 'es', 'fr', 'it'];

const checkLanguageRootPaths = (pathname: string): string | undefined => {
  return rootLanguages.find((language) => {
    return [`/${language}`, `/${language}/`].includes(pathname);
  });
};

export const StartPageLoggedOut: React.FC = () => {
  const trackClickStoreButton = useTrackClickStoreButton();
  const trackClickSerpCategory = useTrackClickSerpCategory();
  const { pathname } = useLocation();
  const trackPageView = useTrackPageView();

  const swimpExperimentVariant = useExperiment('ABACUS-523');
  const isSwimpExperiment =
    swimpExperimentVariant === 'B' || swimpExperimentVariant === 'C';

  useEffect(() => {
    trackPageView();
  }, [trackPageView]);

  useEffect(() => {
    const newLocale = checkLanguageRootPaths(pathname);

    if (newLocale && rootLanguages.includes(newLocale)) {
      const now = new Date();
      const oneYearFromNowDate = new Date(
        now.setFullYear(now.getFullYear() + 1)
      );

      window.document.cookie = `language=${newLocale}; domain=.xing.com; expires=${oneYearFromNowDate.toUTCString()}`;
      window.document.cookie = `language=${newLocale}; expires=${oneYearFromNowDate.toUTCString()}`;
    }
  }, [pathname]);

  return (
    <>
      <DisplayAdsBiddingScript />
      <Head page="startPage" />
      {isSwimpExperiment ? (
        <StyledSwimpSection noBlockPadding noPagePadding>
          <TeaserOnSide />
          <XingletLoader name="@xing-com/crate-startpage-logged-out-swimp-section" />
        </StyledSwimpSection>
      ) : (
        <FindJobsSearchBar
          withTeaser
          isStartPage
          withKeywords
          isSearchAndShuffle
        />
      )}
      <StyledJobsSection noBlockPadding $isSwimpExperiment={isSwimpExperiment}>
        <JobsSection />
      </StyledJobsSection>
      <Section noBlockPadding>
        <LayoutPrimitivesContainer variant="expanded">
          <AdSection placement="top" />
          <SearchInspirationSection />
        </LayoutPrimitivesContainer>
      </Section>
      <CompaniesSection />
      <Section noBlockPadding>
        <LayoutPrimitivesContainer variant="expanded">
          <DeepDiveSection />
        </LayoutPrimitivesContainer>
      </Section>
      <RecruitersSection />
      <Section noBlockPadding>
        <LayoutPrimitivesContainer variant="expanded">
          <AdSection placement="bottom" />
          <NativeAppsSection trackClickStoreButton={trackClickStoreButton} />
          <SeoSection trackClickSerpCategory={trackClickSerpCategory} />
          <RegisterSection />
        </LayoutPrimitivesContainer>
      </Section>
    </>
  );
};

const StartPageLoggedInRedirect: React.FC = () => {
  return (
    <>
      <Redirect to="/jobs/find" noThrow />
      <XingletLoader
        name="@xing-com/crate-communication-root"
        loadingSkeleton
      />
    </>
  );
};

export const StartPage: React.FC = () => {
  const { isLoggedOut } = useLoginState();
  return isLoggedOut ? <StartPageLoggedOut /> : <StartPageLoggedInRedirect />;
};
